import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listDevices, listPatients } from '../lib/adapters';
import { batteryColor, rssiLabel } from '../lib/devices';
import { loadDeviceCatalog, deviceLabelFrom, deviceIconFrom, categoriesFrom, categoryLabel } from '../lib/deviceCatalog';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip } from '../components/StatusChip';
import { SignalBars } from '../components/SignalBars';
import { isImeiType } from '../lib/imei';
import { useRelativeTime } from '../lib/time';
import { ArrowRight, MapPin, Lightning, Alarm } from '@phosphor-icons/react';
import { GatewayList } from '../components/gateways/GatewayList';

export default function Devices() {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('type') || 'all');
  const [devices, setDevices] = useState([]);
  const [patients, setPatients] = useState({});
  const [catalog, setCatalog] = useState([]);

  useEffect(() => { loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([])); }, []);
  // Gateways have their own registry (/gateways), scoped to the org like devices.
  const tabs = ['all', ...categoriesFrom(catalog).filter((c) => c !== 'gateway'), 'gateways'];

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'all') next.delete('type'); else next.set('type', tab);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    listPatients().then(({ items }) => {
      const map = {};
      items.forEach((p) => { map[p.id] = p; });
      setPatients(map);
    });
  }, []);

  useEffect(() => {
    // Category filter goes to the backend as-is; it expands categories server-side.
    if (tab === 'gateways') return;
    listDevices({ type: tab === 'all' || tab === 'gateway' ? undefined : tab }).then(({ items }) => setDevices(items));
  }, [tab]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_devices')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('devices_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">
          {isAdmin ? t('devices_sub_admin') : t('devices_sub')}
        </p>
      </header>

      <div className="flex gap-2 bg-white border border-stone_line rounded-xl p-1 w-fit max-w-full overflow-x-auto" data-testid="devices-tabs">
        {tabs.map((x) => (
          <button
            key={x}
            onClick={() => setTab(x)}
            data-testid={`devices-tab-${x}`}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              tab === x ? 'bg-sage text-white' : 'text-ink-soft hover:bg-bg-alt'
            }`}
          >
            {x === 'all' ? t('all') : x === 'gateways' ? t('gw_title') : categoryLabel(x)}
          </button>
        ))}
      </div>

      {tab === 'gateways' ? <GatewayList /> : (
      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-4 px-6 py-3 border-b border-stone_line bg-bg-alt text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
          <div>{t('nav_devices')}</div>
          <div>{isAdmin ? 'MAC' : t('nav_patients')}</div>
          <div>{isAdmin ? t('nav_patients') : t('battery')}</div>
          <div>{t('battery')}</div>
          <div>{t('device_signal')}</div>
          <div>{t('device_ota_status')}</div>
          <div>{t('last_seen')}</div>
        </div>
        {devices.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="devices-list">
            {devices.map((d) => {
              const Icon = deviceIconFrom(catalog, d.type);
              const owner = patients[d.resident_id];
              const sig = rssiLabel(d.rssi);
              const isEv = isImeiType(d.type);
              const hasLocation = isEv && d.last_position && d.last_position.lat != null;
              return (
                <li key={d.id}>
                  <Link
                    to={`/devices/${d.id}`}
                    data-testid={`device-row-${d.id}`}
                    className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-4 px-6 py-4 items-center hover:bg-bg-alt/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <Icon size={18} weight="duotone" />
                      </div>
                      <div>
                        <div className="text-sm text-ink font-medium flex items-center gap-1.5">
                          {deviceLabelFrom(catalog, d.type, t)}
                          {isEv && (
                            <span
                              title={t('device_personal_alarm')}
                              data-testid={`device-alarm-badge-${d.id}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded-full bg-terracotta-100 text-terracotta-700"
                            >
                              <Alarm size={10} weight="fill" /> SOS
                            </span>
                          )}
                          {hasLocation && (
                            <MapPin size={13} weight="duotone" className="text-sage-600" data-testid={`device-located-${d.id}`} title={t('device_located')} />
                          )}
                        </div>
                        <div className="mt-0.5"><StatusChip status={d.connectivity_status} /></div>
                      </div>
                    </div>
                    <div className="text-xs font-mono text-ink-soft truncate">{isAdmin ? d.mac_address : (owner?.name || '—')}</div>
                    <div className="text-sm text-ink truncate">{isAdmin ? (owner?.name || '—') : (
                      <span className="inline-flex items-center gap-1">
                        {d.battery != null ? `${d.battery}%` : '—'}{d.charging && <Lightning size={13} weight="fill" className="text-sage-600" title={t('device_charging')} />}
                      </span>
                    )}</div>
                    <div className={`text-sm ${batteryColor(d.battery)}`}>
                      <span className="inline-flex items-center gap-1">
                        {d.battery != null ? `${d.battery}%` : '—'}{d.charging && <Lightning size={13} weight="fill" className="text-sage-600" title={t('device_charging')} data-testid={`device-charging-${d.id}`} />}
                      </span>
                    </div>
                    <div className={`text-xs ${sig.cls}`} data-testid={`device-signal-${d.id}`}>
                      {isEv ? <SignalBars score={d.signal_quality} testid={`device-signal-bars-${d.id}`} /> : sig.label}
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full bg-bg-alt text-ink-soft">
                        {t(`ota_${d.ota_status}`)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-ink-soft" data-testid={`device-last-seen-${d.id}`}>{d.last_seen ? rel(d.last_seen) : t('status_never_reported')}</span>
                      <ArrowRight size={14} className="text-ink-muted" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}
